// Lightweight typo matching for advisor and participant lists already in memory.
// Client search is ranked separately by PostgreSQL trigram similarity.
const normalize = (value: string) =>
  value.trim().toLowerCase().replace(/\s+/g, " ");

function withinOneEdit(left: string, right: string): boolean {
  if (Math.abs(left.length - right.length) > 1) return false;
  if (left.length === right.length) {
    const differences = [...left]
      .map((c, i) => (c === right[i] ? -1 : i))
      .filter((i) => i >= 0);
    if (differences.length <= 1) return true;
    const [a, b] = differences;
    return (
      differences.length === 2 &&
      b === a + 1 &&
      left[a] === right[b] &&
      left[b] === right[a]
    );
  }
  const shorter = left.length < right.length ? left : right;
  const longer = left.length < right.length ? right : left;
  let i = 0;
  while (i < shorter.length && shorter[i] === longer[i]) i++;
  return shorter.slice(i) === longer.slice(i + 1);
}

/** Lower scores rank first. Null means no match. Emails are never fuzzy. */
export function nameSearchScore(
  query: string,
  name: string,
  emails: string[] = [],
): number | null {
  const q = normalize(query);
  const fullName = normalize(name);
  if (!q) return 0;
  if (fullName === q) return 0;
  if (
    fullName.includes(q) ||
    emails.some((email) => normalize(email).includes(q))
  )
    return 1;
  if (q.includes("@") || q.length > 100) return null;
  const words = fullName.split(" ").filter(Boolean);
  const terms = q.split(" ");
  // Bipartite matching keeps name tokens distinct without exponential backtracking.
  function matches(allowTypos: boolean): boolean {
    const assigned = new Map<number, number>();
    function assign(termIndex: number, visited: Set<number>): boolean {
      const term = terms[termIndex];
      for (let i = 0; i < words.length; i++) {
        if (visited.has(i)) continue;
        if (
          !words[i].includes(term) &&
          !(allowTypos && term.length >= 4 && withinOneEdit(term, words[i]))
        )
          continue;
        visited.add(i);
        const previous = assigned.get(i);
        if (previous === undefined || assign(previous, visited)) {
          assigned.set(i, termIndex);
          return true;
        }
      }
      return false;
    }
    return terms.every((_, i) => assign(i, new Set()));
  }
  return matches(false) ? 2 : matches(true) ? 3 : null;
}
